import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { supabase } from './supabase';

async function postPasskey(endpoint, body = {}, accessToken = null) {
  const response = await fetch(`/api/v1/${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(body),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || 'Passkey request failed.');
  }
  return payload;
}

function waitForPaint() {
  return new Promise(resolve => {
    requestAnimationFrame(() => setTimeout(resolve, 80));
  });
}

export async function registerPasskey() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sign in before adding a passkey.');

  const start = await postPasskey('passkey-register-start', {}, session.access_token);
  const registration = await startRegistration({ optionsJSON: start.options });
  await postPasskey(
    'passkey-register-finish',
    { response: registration, challengeId: start.challengeId },
    session.access_token,
  );

  return true;
}

export async function loginWithPasskey({ onStage } = {}) {
  onStage?.('preparing_passkey');
  const start = await postPasskey('passkey-login-start');
  onStage?.('waiting_passkey');
  await waitForPaint();
  const authentication = await startAuthentication({ optionsJSON: start.options });
  onStage?.('verifying_passkey');
  const finish = await postPasskey('passkey-login-finish', {
    response: authentication,
    challengeId: start.challengeId,
  });

  onStage?.('creating_session');
  const { error } = await supabase.auth.verifyOtp({
    type: 'magiclink',
    token_hash: finish.tokenHash,
  });
  if (error) throw error;

  onStage?.('complete');
  return true;
}

export async function listPasskeys() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Authentication required.');

  const response = await fetch('/api/v1/passkey-list', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });
  
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result.error || 'Failed to list passkeys.');
  }

  return result.passkeys || [];
}

export async function revokePasskey(id) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Authentication required.');

  await postPasskey('passkey-delete', { id }, session.access_token);
  return true;
}
